import Editor from "~/js/editor";
import {
	type CommentFrameWorkspaceDefinition,
	loadCommentFramesAccordType
} from "~/js/nodes/local/logic/commentFrame";
import { SocketDefinition } from "~/js/nodes/node-definition";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { FileApi } from "../api/fileApi";
import { getPrivateMessageFromError } from "./errorHelpers";
import {
	blobToBase64,
	createBlob,
	helperCreateObjectUrl,
	helperRevokeObjectURL,
	type IFileType
} from "./fileHelpers";
import type { Position } from "./types";
import type { IUserTemplateParams } from "./types/INodeDefinition";

interface ClipboardData {
	data: IUserTemplateParams[];
	commentFrames?: CommentFrameWorkspaceDefinition[];
	version: number;
	type: string;
}

export async function writeDataToClipboard(items: { [mimeType: string]: Blob }) {
	try {
		const clipboardItem = new ClipboardItem(items);
		await navigator.clipboard.write([clipboardItem]);
		return true; // Indicate success
	} catch (error) {
		EventAnalytics.sendErrorInfo("error-writing-to-clipboard", {
			message: getPrivateMessageFromError(error)
		});
		console.error("Failed to write to clipboard:", error);
		return false; // Indicate failure
	}
}

const CLIP_BOARD_FORMAT_VERSION = 1;
const FORMAT_HEADER = "application/phygc";

async function prepareClipboardData(
	nodesAndConnectionsData: IUserTemplateParams[],
	commentFrames?: CommentFrameWorkspaceDefinition[]
): Promise<{ [mimeType: string]: Blob } | null> {
	try {
		// --- Prepare data for clipboard ---
		await mutOutputsToBase64(nodesAndConnectionsData);

		// 1. Custom Format (Primary)
		const clipboardPayload: ClipboardData = {
			data: nodesAndConnectionsData,
			commentFrames,
			version: CLIP_BOARD_FORMAT_VERSION,
			type: FORMAT_HEADER
		};
		const customDataString = JSON.stringify(clipboardPayload);
		// const zipped = lz.compressToBase64(customDataString);
		// if (!zipped) {
		// 	console.error("Failed to compress clipboard data.");
		// 	return null;
		// }
		// Wrap zipped data in a div for HTML format
		const customHtmlData = `<div data-phygc>${customDataString}</div>`;
		const customDataBlob = createBlob(customHtmlData, "text/html");

		// 2. Plain Text Fallback (minimal)
		// Create a simple text representation (e.g., node count or names)
		let plainTextRepresentation = `${nodesAndConnectionsData.length} nodes copied`;
		// Ensure non-empty string for Chrome Android compatibility
		if (!plainTextRepresentation) {
			plainTextRepresentation = " ";
		}
		const plainTextBlob = createBlob(plainTextRepresentation, "text/plain");

		// --- Return multiple formats ---
		// The target application will choose the format it understands best.
		return {
			"text/html": customDataBlob,
			"text/plain": plainTextBlob
		};
	} catch (error) {
		EventAnalytics.sendErrorInfo("error-data-to-clipboard-format", {
			message: getPrivateMessageFromError(error)
		});
		console.error("Error preparing data for clipboard:", error);
		return null; // Indicate failure
	}
}

export async function copiedNodesToClipBoardFormats(
	nodesAndConnectionsData: IUserTemplateParams[],
	commentFrames?: CommentFrameWorkspaceDefinition[]
): Promise<boolean> {
	EventAnalytics.nodeActions.copyNode();
	const clipboardItems = await prepareClipboardData(nodesAndConnectionsData, commentFrames);

	if (!clipboardItems) {
		console.error("Failed to prepare clipboard data. Aborting copy.");
		return false; // Indicate failure
	}

	const success = await writeDataToClipboard(clipboardItems);
	if (!success) {
		console.error("Failed to write prepared data to clipboard.");
	}
	return success;
}

const mutOutputsToBase64 = (config: IUserTemplateParams[]): Promise<undefined[]> => {
	const promises: Promise<undefined>[] = [];
	for (const node of config) {
		for (const output of node.outputSocketGroup) {
			// Skip if output doesn't have the expected structure
			if (!output || typeof output !== "object" || !("optionalInfo" in output)) continue;

			const socketOutput = output as any; // Type assertion for SocketDefinition-like structure
			if (!socketOutput.optionalInfo?.value) continue;
			const file = socketOutput.optionalInfo.value as IFileType;
			if (!file) continue;

			const hasUrl = !!file.objectUrl;
			promises.push(
				(async () => {
					let fileUrl: string | undefined;
					try {
						fileUrl = helperCreateObjectUrl(file);
						const response = await fetch(fileUrl);
						if (!response.ok) {
							throw new Error(`Failed to fetch blob: ${response.statusText}`);
						}
						const blob = await response.blob();
						const encodedFile = await blobToBase64(blob);
						socketOutput.optionalInfo.fileName = file.name || "clipboard_file"; // Use provided name or default
						socketOutput.optionalInfo.value = encodedFile;
						socketOutput.optionalInfo.fileId = file.fileId;
					} catch (error) {
						console.error(
							`Error processing output file for node ${node.id} (output: ${socketOutput.name}):`,
							error
						);
					} finally {
						// Clean up the object URL if we created it specifically for this fetch
						if (!hasUrl && fileUrl) {
							helperRevokeObjectURL(file); // Revoke using the URL string
						}
					}
				})()
			);
		}
	}
	// Promise.allSettled might be better if partial success is acceptable
	return Promise.all(promises);
};
/**
 * Calculates the graph coordinates corresponding to the center of the viewport.
 * @returns {Position} The calculated graph coordinates for the center.
 */
function getGraphCenterCoordinates(): Position {
	const navigation = Editor.context.graph.navigation;
	// Use clientWidth/clientHeight for viewport dimensions
	const screenCenterX = document.body.clientWidth / 2;
	const screenCenterY = document.body.clientHeight / 2;

	// Convert screen center pixels to graph coordinates
	const graphCenterPos = navigation.clientToGraphSpace(screenCenterX, screenCenterY);

	return graphCenterPos;
}

/**
 * Modifies the position of nodes in the provided data array so that
 * the geometric center of their *positions* is aligned with the center
 * of the current view. Maintains the relative positions of all nodes.
 * Modifies the array in place.
 * @param nodesData The array of node data (IUserTemplateParams) to modify.
 */
function calculateGroupOffsets(
	nodesData: IUserTemplateParams[],
	targetCenter: Position,
	commentFrames: CommentFrameWorkspaceDefinition[]
): { offsetX: number; offsetY: number } {
	let minX = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;

	// Include all nodes in bounding box calculation
	for (let i = 0; i < nodesData.length; i++) {
		const node = nodesData[i];
		const pos = node?.position;
		if (pos?.x !== undefined && node?.width !== undefined) {
			minX = Math.min(minX, pos.x);
			maxX = Math.max(maxX, pos.x + node.width);
		}
		if (pos?.y !== undefined && node?.height !== undefined) {
			minY = Math.min(minY, pos.y);
			maxY = Math.max(maxY, pos.y + node.height);
		}
	}

	// Include all comment frames in bounding box calculation
	for (const frame of commentFrames) {
		if (frame.x !== undefined && frame.width !== undefined) {
			minX = Math.min(minX, frame.x);
			maxX = Math.max(maxX, frame.x + frame.width);
		}
		if (frame.y !== undefined && frame.height !== undefined) {
			minY = Math.min(minY, frame.y);
			maxY = Math.max(maxY, frame.y + frame.height);
		}
	}

	// Handle case when no elements exist
	if (
		minX === Number.POSITIVE_INFINITY ||
		maxX === Number.NEGATIVE_INFINITY ||
		minY === Number.POSITIVE_INFINITY ||
		maxY === Number.NEGATIVE_INFINITY
	) {
		return { offsetX: 0, offsetY: 0 };
	}

	// Calculate the geometric center of all elements
	const currentGroupCenterX = minX + (maxX - minX) / 2;
	const currentGroupCenterY = minY + (maxY - minY) / 2;

	// Calculate the offset needed to move the group's center to the target center
	return {
		offsetX: targetCenter.x - currentGroupCenterX,
		offsetY: targetCenter.y - currentGroupCenterY
	};
}

export function updateNodePositionsForCentering(clipBoardData: ClipboardData): void {
	try {
		// 1. Calculate the target center position in graph coordinates
		const targetCenterPos = getGraphCenterCoordinates();
		// Use the new calculateGroupOffsets function
		const { offsetX, offsetY } = calculateGroupOffsets(
			clipBoardData.data || [],
			targetCenterPos,
			clipBoardData.commentFrames || []
		);

		// 5. Apply the offset to ALL original nodes (including those that might have had invalid positions initially,
		//    though they didn't contribute to the bounding box calculation).
		//    If a node initially lacked position, applying offset might be meaningless,
		//    consider setting it directly to the target center or handling it differently.
		for (const node of clipBoardData.data || []) {
			if (
				node.position &&
				typeof node.position.x === "number" &&
				typeof node.position.y === "number"
			) {
				node.position.x = Math.round(node.position.x + offsetX);
				node.position.y = Math.round(node.position.y + offsetY);
			} else {
				// Handle nodes that were filtered out (lacked valid position)
				console.warn(
					`Node ${node.id ?? "(unknown id)"} lacked valid initial position. Placing at calculated screen center.`
				);
				// Assign the calculated center position directly to these nodes
				node.position = {
					x: Math.round(targetCenterPos.x),
					y: Math.round(targetCenterPos.y)
				};
			}
		}
		for (const commentFrame of clipBoardData.commentFrames || []) {
			commentFrame.x = Math.round(commentFrame.x + offsetX);
			commentFrame.y = Math.round(commentFrame.y + offsetY);
		}
	} catch (error) {
		EventAnalytics.sendErrorInfo("error-centering-pasted-nodes", {
			message: getPrivateMessageFromError(error)
		});
		console.error("Error occurred during node position update for centering:", error);
		// Let the paste proceed with original positions if centering fails
	}
}

const loadFilesFromBase64 = async (config: IUserTemplateParams[]): Promise<undefined[]> => {
	const promises: Promise<undefined>[] = [];
	for (const node of config) {
		for (let i = 0; i < node.outputSocketGroup.length; i++) {
			const output = node.outputSocketGroup[i];

			// Type assertion for SocketDefinition-like structure
			const socketOutput = output as any;

			// Only create new SocketDefinition if output has the expected structure
			if (
				socketOutput &&
				typeof socketOutput === "object" &&
				"optionalInfo" in socketOutput
			) {
				//@ts-expect-error
				node.outputSocketGroup[i] = new SocketDefinition(
					socketOutput.name,
					socketOutput.dataType,
					{
						...socketOutput.optionalInfo,
						value: socketOutput.optionalInfo.value
					},
					socketOutput.optional,
					socketOutput?.displayName || socketOutput.definition?.displayName
				);
			}

			const currentOutput = node.outputSocketGroup[i]; // Use the potentially recreated socket

			// Check if value exists and is a string (expected base64)
			if (
				!currentOutput ||
				typeof currentOutput !== "object" ||
				!("dataType" in currentOutput) ||
				!("optionalInfo" in currentOutput) ||
				!(typeof currentOutput.optionalInfo === "object") ||
				!currentOutput.optionalInfo ||
				!("value" in currentOutput.optionalInfo) ||
				currentOutput.dataType === "text" ||
				!currentOutput.optionalInfo?.value ||
				typeof currentOutput.optionalInfo.value !== "string"
			) {
				continue;
			}
			promises.push(
				(async () => {
					try {
						const optionalInfo = currentOutput.optionalInfo as any;
						const base64Data = optionalInfo.value;
						const fileName = optionalInfo.fileName || "pasted_file";
						const fileObject = await FileApi.getFileFromImageSrc(base64Data, fileName);
						// fileObject.fileId = optionalInfo.fileId;
						optionalInfo.value = fileObject; // Update value with IFileType
					} catch (error) {
						console.error(
							`Error loading file from base64 for node ${node.id} (output: ${currentOutput.name}):`,
							error
						);
						// Clear the value or handle error as needed
						(currentOutput.optionalInfo as any).value = null;
					}
				})()
			);
		}
	}
	// Consider Promise.allSettled if partial success is okay
	return Promise.all(promises);
};

const getIsCustomEncoded = (
	htmlString: string
): {
	ok: boolean;
	matchedString: string;
} => {
	const match = htmlString.match(/<div data-phygc[^>]*>(.*)<\/div>/)?.[1];
	return { ok: !!match, matchedString: match || "" };
};

const isOurDecodedData = (data: unknown): data is ClipboardData => {
	if (!data || typeof data !== "object") return false;
	if (!("type" in data)) return false;
	return data.type === FORMAT_HEADER;
};

async function decodeCustomClipboardData(htmlString: string): Promise<ClipboardData | null> {
	try {
		const { ok, matchedString } = getIsCustomEncoded(htmlString);
		if (!ok || !matchedString) {
			// Not our format or empty content
			return null;
		}

		// const decompressedString = lz.decompressFromBase64(matchedString);
		// if (!decompressedString) {
		// 	console.error("Failed to decompress clipboard data.");
		// 	return null;
		// }

		const parsedData = JSON.parse(matchedString);

		if (!isOurDecodedData(parsedData)) {
			console.warn("Clipboard data format validation failed.", parsedData);
			return null;
		}

		return parsedData;
	} catch (error) {
		console.error("Error decoding custom clipboard data:", error);
		throw error;
	}
}

async function processClipboardItem(item: DataTransferItem): Promise<boolean> {
	// --- Check for our custom format first ---
	if (item.kind !== "string" || item.type !== "text/html") {
		return false; // Not the format we are looking for
	}

	try {
		const htmlCompressedString = await new Promise<string>((resolve, _reject) => {
			item.getAsString(resolve);
			// Add a timeout or error handling for getAsString if needed
		});

		const clipboardContent = await decodeCustomClipboardData(htmlCompressedString);

		if (!clipboardContent) {
			// Decoding failed or data wasn't in the expected format
			return false;
		}

		updateNodePositionsForCentering(clipboardContent);
		// --- Load Files and Paste Nodes ---
		await loadFilesFromBase64(clipboardContent.data);
		Editor.context.graph.pasteNodes(clipboardContent.data, false, true);

		loadCommentFramesAccordType(clipboardContent.commentFrames || []);

		return true; // Successfully processed this item
	} catch (error) {
		EventAnalytics.sendErrorInfo("error-processing-clipboard-item", {
			message: getPrivateMessageFromError(error)
		});
		console.error("Failed processing text/html clipboard item:", error);
		return false; // Indicate failure for this item
	}
}

export async function readClipboardData(clipboardData: DataTransfer | null): Promise<void> {
	EventAnalytics.nodeActions.pasteNode();
	if (!clipboardData?.items) {
		EventAnalytics.sendErrorInfo("error-reading-clipboard-data", {
			message: "Clipboard data is null, empty, or inaccessible."
		});
		console.log("Clipboard data is null, empty, or inaccessible.");
		return;
	}

	try {
		let processed = false;
		// Iterate through items to find and process our custom format
		for (let i = 0; i < clipboardData.items.length; i++) {
			const item = clipboardData.items[i];
			processed = await processClipboardItem(item);
			if (processed) {
				break; // Stop processing once custom data is handled
			}
		}

		if (!processed) {
			// Handle fallback to plain text or other formats if desired
			// Example: Check for 'text/plain'
			// for (let i = 0; i < clipboardData.items.length; i++) {
			//     const item = clipboardData.items[i];
			//     if (item.type === 'text/plain') {
			//         // Process plain text...
			//         break;
			//     }
			// }
		}
	} catch (error) {
		EventAnalytics.sendErrorInfo("error-reading-clipboard-data", {
			message: getPrivateMessageFromError(error)
		});
		console.error("An unexpected error occurred during clipboard read/paste:", error);
	}
}
